import React, { useState, useEffect } from 'react';
import { setApiKey } from '../lib/api';

interface LoginPageProps {
  onAuthenticated: () => void;
}

export default function LoginPage({ onAuthenticated }: LoginPageProps) {
  const [apiKey, setApiKeyInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = async (overrideKey?: string) => {
    const key = (overrideKey ?? apiKey).trim();
    if (!key) {
      setError('API key is required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/metrics/enhanced', {
        headers: { 'X-API-Key': key },
      });

      if (res.ok) {
        setApiKey(key);
        onAuthenticated();
      } else if (res.status === 401) {
        setError('Invalid API key. Check the docker compose logs for the bootstrap key.');
      } else {
        setError(`Connection failed (HTTP ${res.status}). Verify the API is running.`);
      }
    } catch {
      setError('Cannot reach the API. Verify Docker is running on port 3000.');
    } finally {
      setLoading(false);
    }
  };

  // SSO hand-off: if the account dashboard passed a short-lived key in the URL
  // fragment (#key=...), auto-connect and scrub it from history immediately.
  useEffect(() => {
    const m = (window.location.hash || '').match(/[#&](?:key|token)=([^&]+)/);
    const urlKey = m && m[1] ? decodeURIComponent(m[1]) : '';
    if (urlKey) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
      setApiKeyInput(urlKey);
      void handleConnect(urlKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleConnect();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1C1917',
        color: '#F5F0E8',
        fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          padding: '0 24px',
        }}
      >
        {/* Logo / branding */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #C2714F 0%, #D4825F 100%)',
              marginBottom: '16px',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              color: '#F5F0E8',
              margin: '0 0 6px',
            }}
          >
            Agentomy
          </h1>
          <p
            style={{
              fontSize: '14px',
              color: '#8F857B',
              margin: 0,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Governance Platform
          </p>
        </div>

        {/* Login card */}
        <div
          style={{
            background: '#292524',
            border: '1px solid #3A352F',
            borderRadius: '12px',
            padding: '32px',
          }}
        >
          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="api-key-input"
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 500,
                color: '#A8A29E',
                marginBottom: '8px',
                letterSpacing: '0.03em',
              }}
            >
              API Key
            </label>
            <input
              id="api-key-input"
              type="password"
              value={apiKey}
              onChange={(e) => { setApiKeyInput(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              placeholder="agentomy_..."
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '14px',
                background: '#211E1C',
                border: error ? '1px solid #F87171' : '1px solid #3A352F',
                borderRadius: '8px',
                color: '#F5F0E8',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) => {
                if (!error) e.target.style.borderColor = '#C2714F';
              }}
              onBlur={(e) => {
                if (!error) e.target.style.borderColor = '#3A352F';
              }}
            />
            {error ? (
              <p
                style={{
                  fontSize: '12px',
                  color: '#F87171',
                  marginTop: '6px',
                  marginBottom: 0,
                }}
              >
                {error}
              </p>
            ) : (
              <p
                style={{
                  fontSize: '12px',
                  color: '#8F857B',
                  marginTop: '6px',
                  marginBottom: 0,
                }}
              >
                Recover the bootstrap key: docker logs agentomy-v2-agentomy-1 | grep BOOTSTRAP
              </p>
            )}
          </div>

          <button
            onClick={() => handleConnect()}
            disabled={loading || !apiKey.trim()}
            style={{
              width: '100%',
              padding: '11px 0',
              fontSize: '14px',
              fontWeight: 600,
              background: loading || !apiKey.trim() ? '#3A352F' : 'linear-gradient(135deg, #C2714F 0%, #D4825F 100%)',
              color: loading || !apiKey.trim() ? '#8F857B' : '#F5F0E8',
              border: 'none',
              borderRadius: '8px',
              cursor: loading || !apiKey.trim() ? 'not-allowed' : 'pointer',
              letterSpacing: '0.03em',
              transition: 'opacity 0.15s',
              fontFamily: 'inherit',
            }}
          >
            {loading ? 'Connecting...' : 'Connect'}
          </button>
        </div>

        {/* Footer note */}
        <p
          style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#6B6259',
            marginTop: '24px',
          }}
        >
          Session ends when the browser tab closes
        </p>
      </div>
    </div>
  );
}
